<template>
  <div class="text-start">
    <!--quizContent title-->
    <v-row no-gutters class="mb-2">
      <v-col>
        <LocalisedContentEditable
          :values="quizContentTitle"
          @update:values="values => (this.quizContentTitle = values)"
          :placeholder="$t('quiz.titleHere')"
          :editable="isEditing"
          css-class="text-h4 font-weight-bold textLight--text"
          :hasError="quizContent.hasInvalidTitle"
          :selectedLocale="selectedLocale"
        />
      </v-col>
    </v-row>

    <!--quizContent creator name and avatar-->
    <v-row no-gutters>
      <v-col>
        <template v-if="creatorNameAndAvatar">
          <v-avatar
            v-if="creatorNameAndAvatar.avatar"
            width="30"
            height="30"
            min-width="30"
            class="me-2"
          >
            <img :src="getImage(creatorNameAndAvatar.avatar)" alt="" />
          </v-avatar>

          <span
            v-if="creatorNameAndAvatar.name"
            class="me-2 caption font-weight-regular textLight--text"
          >
            {{ creatorNameAndAvatar.name }}
          </span>
        </template>

        <template v-if="quizContentCreatedDate">
          <v-icon color="primary" class="me-2"> mdi-calendar-month </v-icon>

          <span class="caption font-weight-regular primary--text">
            {{ quizContentCreatedDate }}
          </span>
        </template>
      </v-col>
    </v-row>

    <!--quizContent isPublic-->
    <v-row no-gutters class="mt-8 mb-6">
      <v-col>
        <div v-if="isEditing" class="d-flex align-center justify-end">
          <span class="pe-3 body-1 font-weight-medium textLight--text">
            {{ `${$t("quiz.isPublic")} ? ` }}
          </span>

          <v-switch
            :input-value="quizIsPublic"
            @change="value => (this.quizIsPublic = value)"
            inset
            flat
            hide-details
            class="ma-0"
            style="max-width: 44px"
          />
        </div>

        <span v-else class="body-1 font-weight-medium textLight--text mb-4">
          {{ `${$t("quiz.isPublic")}: ${quizIsPublic}` }}
        </span>
      </v-col>
    </v-row>

    <!--quizContent description-->
    <v-row no-gutters class="mb-4">
      <v-col>
        <LocalisedContentEditable
          :values="quizContentDescription"
          @update:values="values => (this.quizContentDescription = values)"
          :text-area-rows="5"
          :placeholder="$t('quiz.descriptionHere')"
          :editable="isEditing"
          css-class="body-1 font-weight-light textLight--text"
          :selectedLocale="selectedLocale"
        />
      </v-col>
    </v-row>

    <!--quizContent timeOut-->
    <CourseNumberField
      :label="$t('course.courseDetails.contentTimeOut')"
      :value="quizTimeOut"
      @update:value="value => (this.quizTimeOut = value)"
      :isEditing="isEditing"
      css-class="body-1 font-weight-medium textLight--text"
      class="mb-4"
    />

    <!--quizContent passMark-->
    <CourseNumberField
      :label="$t('course.courseDetails.contentPassMark')"
      :value="quizPassMark"
      @update:value="value => (this.quizPassMark = value)"
      :isEditing="isEditing"
      css-class="body-1 font-weight-medium textLight--text"
      class="mb-4"
    />

    <!--quizContent retryCount-->
    <CourseNumberField
      :label="$t('course.courseDetails.contentRetryCount')"
      :value="quizRetryCount"
      @update:value="value => (this.quizRetryCount = value)"
      :isEditing="isEditing"
      css-class="body-1 font-weight-medium textLight--text"
      class="mb-4"
    />

    <!--quizContent questionsDisplayedCount-->
    <CourseNumberField
      :label="$t('quiz.questionsDisplayedCount')"
      :value="quizQuestionsDisplayedCount"
      @update:value="value => (this.quizQuestionsDisplayedCount = value)"
      :isEditing="isEditing"
      css-class="body-1 font-weight-medium textLight--text"
      class="mb-4"
    />
  </div>
</template>

<script>
import LocalisedContentEditable from "@/app/core-module/components/LocalisedContentEditable";
import CourseNumberField from "@/app/course-module/components/course-details/CourseNumberField";

export default {
  name: "QuizTitle",
  components: { CourseNumberField, LocalisedContentEditable },
  props: {
    quizContent: {
      type: Object,
      required: true,
    },
    isEditing: {
      type: Boolean,
      required: true,
    },
    selectedLocale: {
      type: String,
      default: "en",
    },
  },
  computed: {
    quizContentTitle: {
      get() {
        return this.quizContent.title;
      },
      set(value) {
        this.$emit("update:quizContent", {
          ...this.quizContent,
          title: value,
          hasChangesInTitle: true,
          hasInvalidTitle: !value?.find(x => x.value?.trim()?.length),
        });
      },
    },
    quizContentDescription: {
      get() {
        return this.quizContent.description;
      },
      set(value) {
        this.$emit("update:quizContent", {
          ...this.quizContent,
          description: value,
          hasChangesInDescription: true,
        });
      },
    },
    quizTimeOut: {
      get() {
        return this.quizContent?.quiz?.timeOut;
      },
      set(value) {
        this.$emit("update:quizContent", {
          ...this.quizContent,
          quiz: {
            ...this.quizContent?.quiz,
            timeOut: this.getInteger(value),
          },
          hasChangesInQuiz: true,
        });
      },
    },
    quizPassMark: {
      get() {
        return this.quizContent?.quiz?.passMark;
      },
      set(value) {
        this.$emit("update:quizContent", {
          ...this.quizContent,
          quiz: {
            ...this.quizContent?.quiz,
            passMark: this.getInteger(value),
          },
          hasChangesInQuiz: true,
        });
      },
    },
    quizRetryCount: {
      get() {
        return this.quizContent?.quiz?.retryCount;
      },
      set(value) {
        this.$emit("update:quizContent", {
          ...this.quizContent,
          quiz: {
            ...this.quizContent?.quiz,
            retryCount: this.getInteger(value),
          },
          hasChangesInQuiz: true,
        });
      },
    },
    quizQuestionsDisplayedCount: {
      get() {
        return this.quizContent?.quiz?.questionsDisplayedCount;
      },
      set(value) {
        this.$emit("update:quizContent", {
          ...this.quizContent,
          quiz: {
            ...this.quizContent?.quiz,
            questionsDisplayedCount: this.getInteger(value),
          },
          hasChangesInQuiz: true,
        });
      },
    },
    quizIsPublic: {
      get() {
        return this.quizContent?.isPublic ?? false;
      },
      set(value) {
        this.$emit("update:quizContent", {
          ...this.quizContent,
          isPublic: value,
          hasChangesInIsPublic: true,
        });
      },
    },
    creatorNameAndAvatar() {
      return this.quizContent.createdBy?._id != null
        ? {
            name: this.quizContent.createdBy.firstName,
            avatar: this.quizContent.createdBy.avatar,
          }
        : null;
    },
    quizContentCreatedDate() {
      let formattedDate;

      if (
        this.quizContent.createdBy?.role === "EMPLOYEE" &&
        this.quizContent.appliedAt
      ) {
        formattedDate = `${this.$t("quiz.appliedOn")} `;
        formattedDate += this.$moment(
          parseInt(this.quizContent.appliedAt)
        ).format("DD MMM YYYY");
      } else if (
        this.quizContent.createdBy?.role === "ADMIN" &&
        this.quizContent.createdAt
      ) {
        formattedDate = this.$moment(
          parseInt(this.quizContent.createdAt)
        ).format("DD MMM YYYY");
      }

      return formattedDate;
    },
  },
};
</script>

<style lang="scss" scoped />
